import {ChangeDetectorRef, Component, inject, OnInit, ViewChild} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {ClassificationSchemeDto, SettlementMethodDto} from '|api/codebook';
import {
  DocumentSettlementInnerDto,
  FileForm,
  InternalNotificationKey,
  RetentionTriggerTypeCode,
  SettlementType
} from '|api/commons';
import {
  ApiDocumentService,
  BulkFileSettlementDto,
  BulkFileSettlementPrepareDto,
  DocumentDto,
  FileDto,
  FilePrepareCompletionDto,
  FileSettlementByAcknowledgementCreateDto,
  FileSettlementByAssignmentCreateDto,
  FileSettlementByDocumentCreateDto,
  FileSettlementByOtherMethodCreateDto,
  FileSettlementByRecordCreateDto,
  FileSettlementPrepareDto,
  ValidRetentionTriggerDataRequestDto
} from '|api/document';
import {
  adjustSettlementDate, ApplicationConfigService,
  EsslComponentDto,
  ObjectSettleErrorDialogComponent,
  ResolutionErrorDialogData,
  SettlementInvalidObjects,
  VisibleClassifiersMode,
  WizardComponent,
  WizardStepDirective,
} from '|shared';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiComponentService} from '|api/component';
import {FileSettleFormComponent} from './file-settle-form/file-settle-form.component';
import {AbstractFileSettleCloseDialogComponent} from './abstract-file-settle-close-dialog.component';
import {FileSettleDocumentDetail} from './file-settle-model';
import {
  ButtonComponent,
  FormButtonsComponent,
  TabDirective,
  TabsComponent,
  WaitingDirective
} from '@icz/angular-essentials';
import {
  getStartOfTheDay,
  IczFormArray,
  IczFormControl,
  IczFormGroup,
  IczOption,
  IczValidators,
  TextLength
} from '@icz/angular-form-elements';
import {IczModalService, IczModalSizeClass, injectModalData, injectModalRef} from '@icz/angular-modal';
import {FileSettleStateService} from './file-settle-state.service';
import {GeneralFileSettleFormComponent} from './general-file-settle-form/general-file-settle-form.component';
import {FileSettleFileContentComponent} from './file-settle-file-content/file-settle-file-content.component';
import {
  CheckUnsavedFormDialogService,
  enumToOptions,
  IFormGroupCheckable,
  interpolateBackendErrorMessage,
  isValidNow
} from '@icz/angular-application-core';
import {FileToastService, FileToastType} from '|ui-essl-core';
import {AsyncPipe} from '@angular/common';

export function getSettleFileFormGroup() {
  const todayDateString = new Date().toISOString();

  return new IczFormGroup({
    fileSettlementType: new IczFormControl<Nullable<SettlementType>>(SettlementType.BY_DOCUMENT, [IczValidators.required()]),
    fileTypeId: new IczFormControl<Nullable<number>>(null),
    fileForm: new IczFormControl<Nullable<FileForm>>({value : null, disabled: true}),
    relatedDocumentId: new IczFormControl<Nullable<number>>(null, [IczValidators.required()]), // required if fileSettlementType is BY_DOCUMENT
    content: new IczFormControl<Nullable<string>>(null), // required if fileSettlementType is BY_RECORD
    reason: new IczFormControl<Nullable<string>>(null), // required if fileSettlementType is BY_RECORD
    settlementMethodId: new IczFormControl<Nullable<number>>(null), // for settlement BY_OTHER_METHOD
    settlementDate: new IczFormControl<Nullable<string>>(todayDateString, [IczValidators.required()]),
    entityClassId: new IczFormControl<Nullable<number>>(null, [IczValidators.required()]),
    // read-only fields
    entityClassValidFrom: new IczFormControl<Nullable<string>>({value: null, disabled: true}, []),
    entityClassValidTo: new IczFormControl<Nullable<string>>({value: null, disabled: true}, []),
    doCloseFile: new IczFormControl<Nullable<boolean>>(true),
    closeDate: new IczFormControl<Nullable<string>>({value: todayDateString, disabled: true}),
    disposalOperationCode: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    disposalYear: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    yearOfRetentionPeriodStart: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    retentionTriggerTypeCode: new IczFormControl<Nullable<RetentionTriggerTypeCode>>({value: null, disabled: true}),
    triggerEventCheckYear: new IczFormControl<Nullable<string>>({value: null, disabled: true}, [IczValidators.min((new Date()).getFullYear())]),
    externalRetentionTriggerIds: new IczFormControl<Nullable<number[]>>(null),
    deleteComments: new IczFormControl<Nullable<boolean>>(null),
    documents: new IczFormArray(() => new IczFormGroup({
      documentRefNumber: new IczFormControl<Nullable<string>>(null),
      documentSubject: new IczFormControl<Nullable<string>>(null, [IczValidators.maxLength(100)], undefined, TextLength.UNLIMITED),
      documentId:  new IczFormControl<Nullable<number>>(null),
      documentTypeId:  new IczFormControl<Nullable<number>>(null),
      components: new IczFormControl<Nullable<EsslComponentDto[]>>(null),
      excludedComponentIds: new IczFormControl<Nullable<number[]>>([]),
    }), [])
  });
}

@Component({
  selector: 'icz-file-settle-dialog',
  templateUrl: './file-settle-dialog.component.html',
  styleUrls: ['./file-settle-dialog.component.scss'],
  providers: [CheckUnsavedFormDialogService, FileSettleStateService],
  imports: [WaitingDirective, TabsComponent, TabDirective, GeneralFileSettleFormComponent, FileSettleFormComponent, WizardComponent, WizardStepDirective, FileSettleFileContentComponent, FormButtonsComponent, ButtonComponent, AsyncPipe]
})
export class FileSettleDialogComponent extends AbstractFileSettleCloseDialogComponent implements OnInit, IFormGroupCheckable {

  protected modalRef = injectModalRef<Nullable<boolean>>();
  private apiDocumentService = inject(ApiDocumentService);
  private apiComponentService = inject(ApiComponentService);
  protected fileToastService = inject(FileToastService);
  private cd = inject(ChangeDetectorRef);
  private checkUnsavedService = inject(CheckUnsavedFormDialogService);
  protected override objects = injectModalData<FileDto[]>();
  private modalService = inject(IczModalService);
  private applicationConfigService = inject(ApplicationConfigService);

  @ViewChild('tabsComponent') tabsComponent!: TabsComponent;

  override generalTabForm = getSettleFileFormGroup();

  formGroupsToCheck!: string[];
  fileUnsettledDocuments: DocumentSettlementInnerDto[] = [];
  doCloseFile = true;

  minSettlementDate!: Date;
  documentsInFile: FileSettleDocumentDetail[] = [];
  unsettledDocumentSteps: string[] = [];
  isAllInvalid = false;
  otherSettlementMethodsOptions!: IczOption[];

  showUnsettledDocumentsStepButton = false;

  preselectedComponents: Record<number, EsslComponentDto[]> = {};

  quickSubmit = () => {
    this.submit();
  };

  get showTabs() {
    return this.objects.length || this.isBulk;
  }

  get fileHasUnsettledDocuments() {
    return this.fileUnsettledDocuments.length > 0;
  }

  get isSingleFileSettleValid() {
    return this.getFileTabForm(this.objects[0].id).valid && isNil(this.tabsCache[this.objects[0].id.toString()].validationMessage);
  }

  get singleFileDocuments(): IczFormArray {
    return this.getFileTabForm(this.objects[0].id).get('documents') as IczFormArray;
  }

  get submitButtonLabel() {
    return this.applicationConfigService.closeFileWithSettlement() ? 'Vyřídit a uzavřít' : 'Vyřídit';
  }

  settlementDateForSingleFile(): Date {
    return new Date(this.getFileTabForm(this.objects[0].id).get('settlementDate')!.value);
  }

  classificationSchemes: ClassificationSchemeDto[] = [];
  retentionTriggerTypesOptions: IczOption[] = enumToOptions('retentionTriggerTypeCode', RetentionTriggerTypeCode);

  readonly SettlementType = SettlementType;

  readonly VisibleClassifiersMode = VisibleClassifiersMode;

  bulkFilePrepareData: FileSettlementPrepareDto[] = [];

  bulkSettlementDate: Date = new Date();

  initBulkFileSettlement(requests$: [Observable<ClassificationSchemeDto[]>, Observable<SettlementMethodDto[]>, Observable<BulkFileSettlementPrepareDto>]) {
    this.loadingService.doLoading(
      combineLatest(requests$),
      this
    ).subscribe(([classificationSchemes,
                   otherSettlementMethods,
                   bulkFilePrepare,
                 ]) => {
      this.classificationSchemes = classificationSchemes;
      this.otherSettlementMethodsOptions = otherSettlementMethods.filter(method => isValidNow(method))
        .map(method => ({value: method.id, label: method.name}));
      this.bulkFileSettlementApplyPrepare(bulkFilePrepare);
    });
  }

  private bulkFileSettlementApplyPrepare(bulkFilePrepare: BulkFileSettlementPrepareDto) {
    const invalidDocuments: SettlementInvalidObjects[] = [];
    this.bulkFilePrepareData = bulkFilePrepare.prepareDtos!;
    const originalObjectsCount = this.objects.length;
    this.bulkFilePrepareData.forEach(filePrepare => {
      if (filePrepare.validation) {
        const invalidObjectIndex = this.objects.findIndex(obj => obj.id === filePrepare.fileId)!;
        invalidDocuments.push({object: {
          id: this.objects[invalidObjectIndex].id,
          objectClass: this.objects[invalidObjectIndex].objectClass,
          subject: this.objects[invalidObjectIndex].subject,
        }, validationMessage: interpolateBackendErrorMessage(this.translateService, {errorCode: filePrepare.validation.code!, parameters: filePrepare.validation.parameters!})});
        // prune this.objects from all invalid files for settle
        this.objects.splice(invalidObjectIndex, 1);
      }
    });
    this.isAllInvalid = originalObjectsCount === invalidDocuments.length;
    if (invalidDocuments.length) {
      this.modalService
        .openComponentInModal<void, ResolutionErrorDialogData>({
          component: ObjectSettleErrorDialogComponent,
          modalOptions: {
            sizeClass: IczModalSizeClass.WH_800_600,
            titleTemplate: 'Vybrané spisy není možné vyřídit',
          },
          data: {
            invalidObjects: invalidDocuments,
            isAllInvalid: this.isAllInvalid
          },
        }).subscribe(_ => {
        if (this.isAllInvalid) {
          this.modalRef.close(false);
        }
      });
    }
    if (!this.isAllInvalid) {
      this.initBulkSettlementGeneralForm();
      this.initTabs();
      this.initForms();
      const fileValidRetentionReq: ValidRetentionTriggerDataRequestDto[] = [];
      this.objects.forEach((file: FileDto) => {
        const filePrepareData = this.bulkFilePrepareData.find(d => d.fileId === file.id)!;
        if (filePrepareData) {
          this.getFileTabForm(file.id).get('relatedDocumentId')!.setValue(filePrepareData.documentForSettleId);
          FileSettleFormComponent.prepareEntityClasses(filePrepareData, this.getFileTabForm(file.id), bulkFilePrepare.classificationScheme!, file);
          fileValidRetentionReq.push({
            entityId: file.id,
            entityClassId: filePrepareData.entityClassId
          });

          const documentsArray = this.getFileTabForm(file.id).get('documents') as IczFormArray;
          filePrepareData.documents!.forEach(doc => {
            documentsArray.incrementSize();
            const documentFormGroup = documentsArray.controls[documentsArray.controls.length - 1];
            documentFormGroup.get('documentId')?.setValue(doc.documentId);
            documentFormGroup.get('documentTypeId')?.setValue(doc.documentTypeId);
            documentFormGroup.get('documentRefNumber')?.setValue(doc!.refNumber);
            documentFormGroup.get('documentSubject')?.setValue(doc!.subject);
            documentFormGroup.get('components')?.setValue(this.preselectedComponents[doc.documentId]);
          });
        }

        this.syncFormsValidityWithTabValidity();
      });

      this.loadingService.doLoading(
        this.apiFileService.fileGetValidRetentionTriggerData({body: fileValidRetentionReq}),
        this
      ).subscribe(validRetentions => {
        this.patchBulkObjectRetention(validRetentions);
        this.updateBulkDisposalData();
      });
    }
  }

  initSingleFileSettlement(requests$: [Observable<ClassificationSchemeDto[]>, Observable<SettlementMethodDto[]>, Observable<DocumentDto[]>, Observable<FilePrepareCompletionDto>]) {
    this.loadingService.doLoading(
      combineLatest(requests$),
      this
    ).subscribe(([
                   classificationSchemes,
                   otherSettlementMethods,
                   documentsInFilePage,
                   filePrepare,
                 ]) => {
      this.classificationSchemes = classificationSchemes;
      this.otherSettlementMethodsOptions = otherSettlementMethods.filter(method => isValidNow(method))
        .map(method => ({value: method.id, label: method.name}));
      this.documentsInFile = documentsInFilePage.map((doc: DocumentDto) => ({
        documentId: doc.id!,
        selectedDocumentTypeId: doc.documentTypeId!,
        subject: doc.subject!,
        refNumber: doc.refNumber!
      }));
      this.fileUnsettledDocuments = filePrepare.documents ?? [];
      if (this.applicationConfigService.closeFileWithSettlement()) {
        const doCloseFileCtrl = this.getFileTabForm(this.objects[0].id).get('doCloseFile')!;
        doCloseFileCtrl.disable();
        doCloseFileCtrl.setValue(true);
      }
      if (filePrepare.validation) {
        this.tabsCache[this.objects[0].id.toString()] = {prepared: true, validationMessage: interpolateBackendErrorMessage(this.translateService, {errorCode: filePrepare.validation.code!, parameters: filePrepare.validation.parameters!})};
      } else {
        FileSettleFormComponent.prepareEntityClasses(filePrepare, this.getFileTabForm(this.objects[0].id), filePrepare.classificationScheme!, this.objects[0]);
        this.showUnsettledDocumentsStepButton = this.fileUnsettledDocuments.length > 0;
        this.loadingService.doLoading(this.apiComponentService.componentFindComponentsBySetOfDocumentsIds({body: {documentIds: this.fileUnsettledDocuments.map(doc => doc.documentId)}}),
          this)
          .subscribe(components => {
            components.forEach(component => {
              if (this.preselectedComponents[(component as EsslComponentDto).documentId!]) {
                this.preselectedComponents[(component as EsslComponentDto).documentId!].push((component as EsslComponentDto));
              } else {
                this.preselectedComponents[(component as EsslComponentDto).documentId!] = [(component as EsslComponentDto)];
              }
            });

            const documentsArray = this.getFileTabForm(this.objects[0].id).get('documents') as IczFormArray;
            this.fileUnsettledDocuments?.forEach(doc => {
              this.unsettledDocumentSteps.push('resolveDocuments' + doc.documentId);
              const fileDocument = this.documentsInFile.find(docInFile => docInFile.documentId === doc.documentId);
              documentsArray.incrementSize();
              const documentFormGroup = documentsArray.controls[documentsArray.controls.length - 1];
              documentFormGroup.get('documentId')?.setValue(doc.documentId);
              documentFormGroup.get('documentTypeId')?.setValue(doc.documentTypeId);
              documentFormGroup.get('documentRefNumber')?.setValue(fileDocument!.refNumber);
              documentFormGroup.get('documentSubject')?.setValue(fileDocument!.subject);
              documentFormGroup.get('components')?.setValue(this.preselectedComponents[doc.documentId]);
            });
          });
      }
    });
  }

  private initForms() {
    this.objects.forEach(f => {
      const newFileForm = getSettleFileFormGroup();
      newFileForm.get('fileTypeId')!.setValue(f.fileTypeId);
      if (this.applicationConfigService.closeFileWithSettlement()) {
        const doCloseFileCtrl = newFileForm.get('doCloseFile')!;
        doCloseFileCtrl.disable();
        doCloseFileCtrl.setValue(true);
      }
      if (f.caseFile) {
        newFileForm.get('entityClassId')!.setValue(f.entityClassId);
      }
      this.forms[f.id] = newFileForm;
      this.tabsCache[f.id.toString()] = {prepared: false};
    });
  }

  private initBulkSettlementGeneralForm() {
    if (this.objects.every(d => d.entityClassId === this.objects[0].entityClassId)) {
      this.generalTabForm.get('entityClassId')!.setValue(this.objects[0].entityClassId);
    }
    if (this.objects.every(d => d.fileTypeId === this.objects[0].fileTypeId)) {
      this.generalTabForm.get('fileTypeId')!.setValue(this.objects[0].fileTypeId);
    }

    this.generalTabForm.get('entityClassId')!.clearValidators();
    if (this.applicationConfigService.closeFileWithSettlement()) {
      const generalDoCloseFileCtrl = this.generalTabForm.get('doCloseFile')!;
      generalDoCloseFileCtrl.disable();
      generalDoCloseFileCtrl.setValue(true);
    }
    this.generalTabForm.get('settlementDate')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(settlementDate => {
      if (settlementDate) {
        this.bulkSettlementDate = new Date(settlementDate);
        this.loadingService.doLoading(
          this.apiFileService.filePrepareBulkSettlement({dateTime: settlementDate, body: this.objects.map(f => f.id)}),
          this
        ).subscribe(bulkPrepare => {
          this.bulkFileSettlementApplyPrepare(bulkPrepare);
        });
      }
    });

    const overrideFields: string[] = [
      'fileSettlementType',
      'fileTypeId',
      'settlementDate',
      'content',
      'reason',
      'settlementMethodId',
      'doCloseFile',
      'yearOfRetentionPeriodStart',
      'triggerEventCheckYear',
      'entityClassId'
    ];
    this.setupGeneralTabValueOverride(overrideFields);

    this.generalTabForm.get('entityClassId')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef))
      .subscribe(eClassId => {
        if (eClassId && this.entityClasses.length) {
          const entityClass = this.entityClasses.find(ec => ec.id === eClassId)!;
          this.generalTabForm.get('entityClassValidFrom')!.setValue(entityClass.validFrom);
          this.generalTabForm.get('entityClassValidTo')!.setValue(entityClass.validTo);

          this.loadingService.doLoading(
            this.apiFileService.fileGetValidRetentionTriggerData({
              body: this.objects.map(obj => ({
                entityId: obj.id,
                entityClassId: this.getFileTabForm(obj.id).get('entityClassId')!.value
              }))
            }),
            this
          ).subscribe(validRetentionData => {
            this.patchBulkObjectRetention(validRetentionData);
          });
        }
      });

    // listener for close file together with settle file in 1 UI dialog
    this.generalTabForm.get('doCloseFile')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(shouldClose => {
      if (shouldClose) {
        this.generalTabForm.get('closeDate')!.setValue(new Date().toISOString());
      }
    });

    this.generalTabForm.get('fileSettlementType')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(fileSettlementType => {
      this.processReasonAndContentFields(fileSettlementType, this.generalTabForm);
      Object.values(this.forms).forEach(formGroup => {
        const settlingDocumentControl = formGroup.get('relatedDocumentId')!;

        if (fileSettlementType === SettlementType.BY_DOCUMENT || fileSettlementType === SettlementType.BY_ASSIGNMENT) {
          settlingDocumentControl.setValidators([IczValidators.required()]);
        }
        else {
          settlingDocumentControl.setValidators([]);
        }

        this.processReasonAndContentFields(fileSettlementType, formGroup);
      });
      this.syncFormsValidityWithTabValidity();
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.isBulk = this.objects.length > 1;

    const newestDate = new Date(Math.max(...this.objects.map(f => (new Date(f.auditInfo!.createdAt!)).getTime())));
    this.minSettlementDate = getStartOfTheDay(newestDate);
    this.checkUnsavedService.addUnsavedFormCheck(this, ['generalTabForm']);

    if (!this.objects?.length) return;

    if (this.isBulk) {
      const settlementDate = this.generalTabForm.get('settlementDate')!.value!;
      this.generalTabForm.addControl('useGlobalSettings', new IczFormControl(false));
      this.initBulkFileSettlement([
        this.codebookService.classificationSchemes(),
        this.codebookService.otherSettlementMethods(),
        this.apiFileService.filePrepareBulkSettlement({dateTime: settlementDate, body: this.objects.map(f => f.id)}),
      ]);
    } else {
      this.initTabs();
      this.initForms();
      this.initSingleFileSettlement([
        this.codebookService.classificationSchemes(),
        this.codebookService.otherSettlementMethods(),
        this.apiDocumentService.documentFindByFileId({fileId: this.objects[0].id}),
        this.apiFileService.filePrepareSettlement({
          dateTime: new Date().toISOString(),
          id: this.objects[0].id,
        })
      ]);
    }
  }

  private getSingleFileSettleByAcknowledgment(formValue: BulkFileSettlementDto, innerSettlementDocuments: DocumentSettlementInnerDto[], fileId?: number): FileSettlementByAcknowledgementCreateDto | BulkFileSettlementDto {
    const result =  {
      disposalScheduleId: formValue.disposalScheduleId,
      documents: innerSettlementDocuments,
      fileTypeId: formValue.fileTypeId,
      settlementDate: adjustSettlementDate(formValue.settlementDate!),
      entityClassId: formValue.entityClassId!,
      doCloseFile: formValue.doCloseFile,
      yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
      triggerEventCheckYear: formValue.triggerEventCheckYear,
    };
    if (fileId) {
      (result as BulkFileSettlementDto).settlementType = SettlementType.BY_ACKNOWLEDGEMENT;
      (result as BulkFileSettlementDto).fileId = fileId;
    }
    return result;
  }

  private getSingleFileSettleByAssignment(formValue: BulkFileSettlementDto, innerSettlementDocuments: DocumentSettlementInnerDto[], fileId?: number): FileSettlementByAssignmentCreateDto | BulkFileSettlementDto {
    const result =  {
      relatedDocumentId: formValue.relatedDocumentId!,
      disposalScheduleId: formValue.disposalScheduleId,
      documents: innerSettlementDocuments,
      fileTypeId: formValue.fileTypeId,
      settlementDate: adjustSettlementDate(formValue.settlementDate!),
      entityClassId: formValue.entityClassId!,
      doCloseFile: formValue.doCloseFile,
      yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
      triggerEventCheckYear: formValue.triggerEventCheckYear,
    };
    if (fileId) {
      (result as BulkFileSettlementDto).settlementType = SettlementType.BY_ASSIGNMENT;
      (result as BulkFileSettlementDto).fileId = fileId;
    }
    return result;
  }

  private getSingleFileSettleByRecord(formValue: BulkFileSettlementDto, innerSettlementDocuments: DocumentSettlementInnerDto[], fileId?: number): FileSettlementByRecordCreateDto | BulkFileSettlementDto {
    const result =  {
      content: formValue.content!,
      reason: formValue.reason!,
      disposalScheduleId: formValue.disposalScheduleId,
      documents: innerSettlementDocuments,
      fileTypeId: formValue.fileTypeId,
      settlementDate: adjustSettlementDate(formValue.settlementDate!),
      entityClassId: formValue.entityClassId!,
      doCloseFile: formValue.doCloseFile,
      yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
      triggerEventCheckYear: formValue.triggerEventCheckYear,
    };
    if (fileId) {
      (result as BulkFileSettlementDto).settlementType = SettlementType.BY_RECORD;
      (result as BulkFileSettlementDto).fileId = fileId;
    }
    return result;
  }

  private getSingleFileSettleByOtherMethod(formValue: BulkFileSettlementDto, innerSettlementDocuments: DocumentSettlementInnerDto[], fileId?: number): FileSettlementByOtherMethodCreateDto | BulkFileSettlementDto {
    const result =  {
      settlementMethodId: formValue.settlementMethodId,
      disposalScheduleId: formValue.disposalScheduleId,
      documents: innerSettlementDocuments,
      fileTypeId: formValue.fileTypeId,
      settlementDate: adjustSettlementDate(formValue.settlementDate!),
      entityClassId: formValue.entityClassId!,
      doCloseFile: formValue.doCloseFile,
      yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
      triggerEventCheckYear: formValue.triggerEventCheckYear,
    };
    if (fileId) {
      (result as BulkFileSettlementDto).settlementType = SettlementType.BY_RECORD;
      (result as BulkFileSettlementDto).fileId = fileId;
    }
    return result;
  }

  private getSingleFileSettleByDocument(formValue: BulkFileSettlementDto, innerSettlementDocuments: DocumentSettlementInnerDto[], fileId?: number): FileSettlementByDocumentCreateDto | BulkFileSettlementDto {
    const result =  {
      relatedDocumentId: formValue.relatedDocumentId!,
      disposalScheduleId: formValue.disposalScheduleId,
      documents: innerSettlementDocuments,
      fileTypeId: formValue.fileTypeId,
      settlementDate: adjustSettlementDate(formValue.settlementDate!),
      entityClassId: formValue.entityClassId!,
      doCloseFile: formValue.doCloseFile,
      yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
      triggerEventCheckYear: formValue.triggerEventCheckYear,
    };
    if (fileId) {
      (result as BulkFileSettlementDto).settlementType = SettlementType.BY_DOCUMENT;
      (result as BulkFileSettlementDto).fileId = fileId;
    }
    return result;
  }

  submit() {
    if (this.isBulk) {
      this.toggleShowValidity(true);

      const allFormsValid = Object.values(this.forms).every(f => f.valid);
      if (allFormsValid) {
        const body: Array<BulkFileSettlementDto> = [];

        Object.entries(this.forms).forEach(([fileId, f]) => {
          const formValue = f.getRawValue() as BulkFileSettlementDto;

          const innerSettlementDocuments: DocumentSettlementInnerDto[] = formValue.documents!.map((d: any) => ({
            documentId: d.documentId!,
            documentTypeId: d.documentTypeId!,
            excludedComponentIds: d.excludedComponentIds,
          }));

          const fileSettlementType = f.get('fileSettlementType')!.value;
          if (fileSettlementType === SettlementType.BY_ASSIGNMENT) {
            body.push(this.getSingleFileSettleByAssignment(f.getRawValue() as BulkFileSettlementDto, innerSettlementDocuments, parseInt(fileId)) as BulkFileSettlementDto);
          } else if (fileSettlementType === SettlementType.BY_RECORD) {
            body.push(this.getSingleFileSettleByRecord(f.getRawValue(), innerSettlementDocuments, parseInt(fileId)) as BulkFileSettlementDto);
          } else if (fileSettlementType === SettlementType.BY_DOCUMENT) {
            body.push(this.getSingleFileSettleByDocument(f.getRawValue(), innerSettlementDocuments, parseInt(fileId)) as BulkFileSettlementDto);
          } else if (fileSettlementType === SettlementType.BY_ACKNOWLEDGEMENT) {
            body.push(this.getSingleFileSettleByAcknowledgment(f.getRawValue(), innerSettlementDocuments, parseInt(fileId)) as BulkFileSettlementDto);
          } else if (fileSettlementType === SettlementType.BY_OTHER_METHOD) {
            body.push(this.getSingleFileSettleByOtherMethod(f.getRawValue(), innerSettlementDocuments, parseInt(fileId)) as BulkFileSettlementDto);
          }
        });

        this.loadingService.doLoading(this.apiFileService.fileBulkSettle({body}), this)
          .subscribe({
            next: _ => {
              this.fileToastService.dispatchBulkOperationStartedToast(FileToastType.BULK_FILE_SETTLEMENT_START_SUCCESS, {
                [InternalNotificationKey.COUNT]: this.objects.length,
              });
              this.modalRef.close(true);
            },
            error: _ => {
              this.fileToastService.dispatchFileSettleErrorToast(this.objects.map(f => ({
                [InternalNotificationKey.FILE_ID]: f.id,
                [InternalNotificationKey.FILE_SUBJECT]: f.subject!,
              })));
              this.modalRef.close(false);
            },
          });
      }
      else {
        this.markFirstInvalidFormAsTouched();
        this.tabsComponent.selectAndScrollToFirstInvalid();
      }
    }
    else {
      const fileToSettle = this.objects[0];
      const formValue = this.getFileTabForm(fileToSettle.id).getRawValue();
      let request$ = new Observable<unknown>();

      const innerSettlementDocuments: DocumentSettlementInnerDto[] = formValue.documents.map((d: any) => ({
        documentId: d.documentId!,
        documentTypeId: d.documentTypeId!,
        excludedComponentIds: d.excludedComponentIds,
      }));

      if (formValue.fileSettlementType === SettlementType.BY_ACKNOWLEDGEMENT) {
        request$ = this.apiFileService.fileSettleFileByAcknowledgement(
          {
            id: fileToSettle.id,
            body: this.getSingleFileSettleByAcknowledgment(formValue, innerSettlementDocuments) as FileSettlementByAcknowledgementCreateDto,
          });
      } else if (formValue.fileSettlementType === SettlementType.BY_ASSIGNMENT) {
        request$ = this.apiFileService.fileSettleFileByAssignment(
          {
            id: fileToSettle.id,
            body: this.getSingleFileSettleByAssignment(formValue, innerSettlementDocuments) as FileSettlementByAssignmentCreateDto,
          }
        );
      } else if (formValue.fileSettlementType === SettlementType.BY_DOCUMENT) {
        request$ = this.apiFileService.fileSettleFileByDocument(
          {
            id: fileToSettle.id,
            body: this.getSingleFileSettleByDocument(formValue, innerSettlementDocuments) as FileSettlementByDocumentCreateDto,
          }
        );
      } else if (formValue.fileSettlementType === SettlementType.BY_RECORD) {
        request$ = this.apiFileService.fileSettleFileByRecord(
          {
            id: fileToSettle.id,
            body: this.getSingleFileSettleByRecord(formValue, innerSettlementDocuments) as FileSettlementByRecordCreateDto,
          },
        );
      } else if (formValue.fileSettlementType === SettlementType.BY_OTHER_METHOD) {
        request$ = this.apiFileService.fileSettleFileByOtherMethod(
          {
            id: fileToSettle.id,
            body: this.getSingleFileSettleByOtherMethod(formValue, innerSettlementDocuments) as FileSettlementByOtherMethodCreateDto,
          },
        );
      }

      this.loadingService.doLoading(
        request$,
        this
      ).subscribe({
        next: _ => {
          this.fileToastService.dispatchFileWarningToast(FileToastType.FILE_SETTLEMENT_STARTED, {
            [InternalNotificationKey.FILE_ID]: fileToSettle.id,
            [InternalNotificationKey.FILE_SUBJECT]: fileToSettle.subject!,
          });
          this.close(true);
        },
        error: _ => {
          this.fileToastService.dispatchFileSettleErrorToast([{
            [InternalNotificationKey.FILE_ID]: fileToSettle.id,
            [InternalNotificationKey.FILE_SUBJECT]: fileToSettle.subject!,
          }]);
          // Close the dialog so the view refreshes and the file gets "unblocked" for the user.
          this.close(false);
        },
      });
    }
  }

  close(isDone?: boolean) {
    this.modalRef.close(isDone);
  }
}
